import React, { useState, useRef, useEffect } from 'react';
import { useOutsideClick } from '../../utils/helper';

/**
 * Autocomplete Component
 * Searchable dropdown with API integration
 * @param {Object} props
 * @param {String} props.label - Label for the input
 * @param {String} props.placeholder - Placeholder text
 * @param {Array} props.options - Array of options to display
 * @param {Function} props.onSearch - Callback when search text changes (debounced)
 * @param {Function} props.onSelect - Callback when option is selected
 * @param {String} props.value - Selected value ID
 * @param {String} props.displayValue - Display text for selected value
 * @param {Boolean} props.loading - Loading state
 * @param {Boolean} props.required - Is field required
 * @param {String} props.error - Error message
 * @param {String} props.displayKey - Key to use for display text (default: 'name')
 * @param {String} props.valueKey - Key to use for value (default: '_id')
 */
const Autocomplete = ({
  label,
  placeholder = 'Search...',
  options = [],
  onSearch,
  onSelect,
  value,
  displayValue,
  loading = false,
  required = false,
  error = '',
  displayKey = 'name',
  valueKey = '_id',
  inputClassName = '',
  labelClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useOutsideClick(wrapperRef, () => {
    setIsOpen(false);
    setSearchText('');
  });

  // Debounce search - only trigger when user actually types
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const currentSearch = searchText.trim();

    // If user is typing, debounce the search
    if (currentSearch) {
      // Don't call API if search term hasn't changed
      if (currentSearch === lastSearchTerm) {
        return;
      }

      const delayDebounceFn = setTimeout(() => {
        setLastSearchTerm(currentSearch);
        onSearch(currentSearch);
      }, 400);

      return () => clearTimeout(delayDebounceFn);
    }
    // If search is empty and we haven't loaded initial data yet, load it once
    else if (!hasLoadedInitial && !currentSearch && options.length === 0) {
      setHasLoadedInitial(true);
      setLastSearchTerm('');
      onSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, isOpen]);

  const handleInputChange = (e) => {
    setSearchText(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    onSelect(option);
    setSearchText('');
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Don't trigger onSearch here - let useEffect handle it
  };

  const getDisplayText = () => {
    if (displayValue) return displayValue;
    if (value && options.length > 0) {
      const selected = options.find((opt) => opt[valueKey] === value);
      return selected ? selected[displayKey] : '';
    }
    return '';
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {label && (
        <label
          className={
            labelClassName || 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
          }
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <input
        type="text"
        className={
          inputClassName
            ? `${inputClassName} ${error ? 'border-red-500' : ''}`
            : `w-full p-3 border ${
                error ? 'border-red-500' : 'border-gray-300 dark:border-shoorah-darkBorder'
              } rounded-lg focus:ring-2 focus:ring-shoorah-primary dark:bg-gray-700 dark:text-white`
        }
        placeholder={placeholder}
        value={isOpen ? searchText : getDisplayText()}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        autoComplete="off"
      />

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-shoorah-darkBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          )}

          {!loading && options.length === 0 && (
            <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
              {searchText ? 'No results found' : 'Start typing to search...'}
            </div>
          )}

          {!loading &&
            options.length > 0 &&
            options.map((option, index) => (
              <div
                key={option[valueKey] || index}
                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                onClick={() => handleOptionClick(option)}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {option[displayKey]}
                </div>
                {option.email && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">{option.email}</div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
